import type { MenuGroupOption, MenuOption } from 'naive-ui'
import type { VNodeChild } from 'vue'

import type { NavigationItem, NavigationItemGroup } from '../model/useNavigation'

function renderNavItemIcon(item: NavigationItem): VNodeChild {
  if (item.icon) {
    return h(item.icon)
  }
  if (item.iconClass) {
    return h('i', { class: item.iconClass })
  }
  return null
}

interface Params {
  navigation: ComputedRef<NavigationItemGroup[]>
  isMobile: ComputedRef<boolean>
  onCloseSidebar: () => void
}

export function useSidebarMenu({ navigation, isMobile, onCloseSidebar }: Params) {
  const route = useRoute()
  const router = useRouter()
  const isSignoutPopupOpen = ref(false)

  const menuOptions = computed<MenuOption[]>(() =>
    navigation.value.flatMap((segment, idx) => {
      const visibleItems = segment.items.filter((item) => item.show)
      if (!visibleItems.length) {
        return []
      }

      const children: MenuOption[] = visibleItems.map((item) => ({
        key: item.path,
        label: item.name,
        icon: () => renderNavItemIcon(item),
      }))

      if (!segment.groupName) {
        return children
      }

      const group: MenuGroupOption = {
        key: `group-${idx}`,
        type: 'group',
        label: segment.groupName,
        children,
      }
      return [group]
    }),
  )

  const itemsByKey = computed(
    () => new Map(navigation.value.flatMap((segment) => segment.items).map((item) => [item.path, item])),
  )

  const activeKey = computed<string | null>(() => {
    if (route.path === '/') {
      return '/'
    }
    const firstSegment = `/${route.path.split('/')[1]}`
    const allItems = navigation.value.flatMap((segment) => segment.items)
    const match = allItems.find((item) => {
      if (item.path === '/' || item.target === '_blank') {
        return false
      }
      return `/${item.path.split('/')[1].split('?')[0]}` === firstSegment
    })
    return match?.path ?? null
  })

  const handleMenuSelect = (key: string) => {
    if (key === '/signout') {
      isSignoutPopupOpen.value = true
      return
    }

    if (isMobile.value) {
      document.body.style.overflow = 'visible'
      document.body.style.height = 'auto'
      onCloseSidebar()
    }

    if (itemsByKey.value.get(key)?.target === '_blank') {
      window.open(key, '_blank', 'noopener,noreferrer')
      return
    }

    router.push(key)
  }

  return { menuOptions, activeKey, handleMenuSelect, isSignoutPopupOpen }
}
